import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {requestPassword} from '../core/_requests'

const initialValues = {
  email: '',
}

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Formato de email incorrecto')
    .min(3, 'Mínimo 3 símbolos')
    .max(50, 'Máximo 50 símbolos')
    .required('El email es requerido'),
})

export function ForgotPassword() {
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [emailSent, setEmailSent] = useState(false)
  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)
      setErrorMessage('')
      
      requestPassword(values.email)
        .then(({data}) => {
          setHasErrors(false)
          setEmailSent(true)
          setLoading(false)
          setSubmitting(false)
        })
        .catch((error) => {
          setHasErrors(true)
          setLoading(false)
          setSubmitting(false)
          
          // Handle different error responses
          if (error.response?.data?.message) {
            setErrorMessage(error.response.data.message)
          } else if (error.response?.status === 404) {
            setErrorMessage('No se encontró un usuario con ese email')
          } else {
            setErrorMessage('Ocurrió un error, intente nuevamente')
          }
          
          setStatus(errorMessage)
        })
    },
  })

  // Si el email fue enviado exitosamente, mostrar solo el mensaje de éxito
  if (emailSent) {
    return (
      <div className='w-100'>
        <div className='text-center mb-10'>
          <h1 className='text-dark fw-bolder mb-3'>Correo enviado</h1>
        </div>

        <div className='mb-10 p-8 rounded' style={{backgroundColor: 'rgba(115,188,124,0.15)'}}>
          <div className='text-center' style={{color: '#36553c'}}>
            <div className='fs-3 fw-bold mb-3'>✉️</div>
            <div>Se ha enviado un correo electrónico con las instrucciones para la recuperación de su contraseña.</div>
            <div className='mt-3'>Por favor, revisar su bandeja de entrada, y si no lo encuentra, revisar su carpeta de correo no deseado.</div>
            <div className='mt-3' style={{fontWeight: 600}}>El correo puede llegar durante los próximos 5 minutos.</div>
          </div>
        </div>

        <div className='text-center'>
          <Link to='/auth/login'>
            <button type='button' className='btn text-white fw-bold' style={{backgroundColor: '#73bc7c'}}>
              Volver al Login
            </button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        {/* begin::Title */}
        <h1 className='text-dark fw-bolder mb-3'>Recuperación de contraseña</h1>
        {/* end::Title */}

        {/* begin::Link */}
        <div className='text-gray-500 fw-semibold fs-6'>
          Introduzca su correo electrónico para restablecer su contraseña.
        </div>
        {/* end::Link */}
      </div>

      {/* begin::Title */}
      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            {errorMessage || 'Ocurrió un error, intente nuevamente.'}
          </div>
        </div>
      )}
      {/* end::Title */}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-gray-900 fs-6'>Email</label>
        <input
          type='email'
          placeholder=''
          autoComplete='off'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <button 
          type='submit' 
          id='kt_password_reset_submit' 
          className='btn text-white fw-bold me-4'
          style={{backgroundColor: '#73bc7c'}}
          disabled={formik.isSubmitting || loading}
        >
          {!loading && <span className='indicator-label'>Recuperar Contraseña</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Por favor espere...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_password_reset_form_cancel_button'
            className='btn btn-light'
            disabled={formik.isSubmitting || loading}
          >
            Volver
          </button>
        </Link>{' '}
      </div>
      {/* end::Form group */}
    </form>
  )
}
